import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClipboardList,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import type {
  ActiveTask,
  MobilisationEvent,
  RejectEmailRecipient,
} from '@hcc/types';
import { Button } from '../ui/Button';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Pill } from '../ui/Pill';
import { api, ApiError } from '../../lib/api';
import { projectTasksClientSide } from '../../lib/projectTasks';
import { AssignTaskModal, VISIBILITY_LABELS } from './AssignTaskModal';
import { RejectTaskModal } from '../onboarding/RejectTaskModal';

type PillTone = 'success' | 'warning' | 'danger' | 'neutral' | 'accent';

const STATUS_LABELS: Record<
  ActiveTask['status'],
  { label: string; tone: PillTone; icon: LucideIcon }
> = {
  pending: { label: 'To do', tone: 'neutral', icon: ClipboardList },
  awaiting_review: { label: 'Awaiting review', tone: 'warning', icon: ClipboardList },
  changes_requested: { label: 'Changes requested', tone: 'danger', icon: ClipboardList },
  complete: { label: 'Complete', tone: 'success', icon: ShieldCheck },
};

/**
 * Admin task panel for the per-person edit page. Shows the projected
 * tasks for this person and lets HR / coord / admin assign new ones,
 * approve / reject awaiting submissions, and remove. Approve / reject
 * post to the existing POST /api/people/:id/events route which is
 * already gated to the right role set.
 */
export function TasksPanel({
  personId,
  events,
  onChange,
}: {
  personId: string;
  events: MobilisationEvent[] | null;
  onChange: () => void;
}) {
  // Plain to-do tasks only. Onboarding form/document/upload tasks are managed
  // in the Forms / Documents / Library panels and shown in Onboarding status,
  // so listing them here too would duplicate them.
  const tasks = useMemo(
    () => projectTasksClientSide(events ?? []).filter((t) => !t.onboarding_kind),
    [events],
  );
  const [assigning, setAssigning] = useState(false);
  const [editing, setEditing] = useState<ActiveTask | null>(null);
  const [rejecting, setRejecting] = useState<ActiveTask | null>(null);
  const [rejectBusy, setRejectBusy] = useState(false);

  // The rejection email defaults to the personal address, which only the
  // admin-only person GET exposes. Fetched lazily when the reject dialog
  // opens; everyone who can reject (HR/coordinator/admin) passes its gate.
  const emailsQ = useQuery({
    queryKey: ['admin', 'people', personId, 'reject-emails'],
    enabled: !!rejecting,
    queryFn: async () =>
      (
        await api<{ person: { email: string; personal_email?: string | null } }>(
          `/api/admin/people/${personId}`,
        )
      ).person,
  });

  async function onReject(reason: string, recipient: RejectEmailRecipient) {
    if (!rejecting) return;
    setRejectBusy(true);
    try {
      await api(`/api/people/${personId}/events`, {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'TaskRejected',
          payload: { task_id: rejecting.task_id, reason, recipient },
        }),
      });
      toast.success('Rejection sent — the inductee will be emailed.');
      setRejecting(null);
      onChange();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'reject failed');
    } finally {
      setRejectBusy(false);
    }
  }

  return (
    <CollapsibleSection
      defaultOpen
      title="Tasks"
      icon={<ClipboardList className="h-4 w-4 text-fg-muted" />}
      trailing={
        <Button
          variant="primary"
          size="sm"
          iconLeft={<Plus className="h-3.5 w-3.5" />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setAssigning(true);
          }}
        >
          Assign task
        </Button>
      }
    >
      {tasks.length === 0 ? (
        <p className="px-4 py-3 text-sm text-fg-muted">
          No tasks for this person yet.
        </p>
      ) : (
        <ul className="divide-y divide-divider/10">
          {tasks.map((t) => (
            <TaskRow
              key={t.task_id}
              personId={personId}
              task={t}
              onChange={onChange}
              onEdit={() => setEditing(t)}
              onReject={() => setRejecting(t)}
            />
          ))}
        </ul>
      )}
      <AssignTaskModal
        open={assigning}
        personId={personId}
        onClose={() => setAssigning(false)}
        onDone={() => {
          setAssigning(false);
          onChange();
        }}
      />
      <AssignTaskModal
        open={!!editing}
        personId={personId}
        editing={editing}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          onChange();
        }}
      />
      <RejectTaskModal
        open={!!rejecting}
        taskTitle={rejecting?.title ?? ''}
        workEmail={emailsQ.data?.email ?? ''}
        personalEmail={emailsQ.data?.personal_email ?? null}
        busy={rejectBusy}
        onCancel={() => setRejecting(null)}
        onSubmit={onReject}
      />
    </CollapsibleSection>
  );
}

function TaskRow({
  personId,
  task,
  onChange,
  onEdit,
  onReject,
}: {
  personId: string;
  task: ActiveTask;
  onChange: () => void;
  onEdit: () => void;
  onReject: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const status = STATUS_LABELS[task.status];
  const StatusIcon = status.icon;

  async function approve() {
    setBusy(true);
    try {
      await api(`/api/people/${personId}/events`, {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'TaskApproved',
          payload: { task_id: task.task_id },
        }),
      });
      toast.success(`${task.title} approved`);
      onChange();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'approve failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove "${task.title}"?`)) return;
    setBusy(true);
    try {
      await api(`/api/people/${personId}/events`, {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'TaskRemoved',
          payload: { task_id: task.task_id },
        }),
      });
      toast.success('Task removed');
      onChange();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'remove failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{task.title}</span>
            <Pill tone={status.tone} size="md" icon={<StatusIcon className="h-3 w-3" />}>
              {status.label}
            </Pill>
            {task.due_at ? (
              <span className="text-[11px] tabular-nums text-fg-dim">
                Due {task.due_at}
              </span>
            ) : null}
            {task.approval_required ? (
              <Pill tone="neutral" size="sm">
                Approval req.
              </Pill>
            ) : null}
          </div>
          {task.description ? (
            <p className="mt-1 whitespace-pre-wrap text-xs text-fg-muted">
              {task.description}
            </p>
          ) : null}
          {task.visibility.length > 1 ? (
            <p className="mt-1 text-[11px] text-fg-dim">
              Also visible to:{' '}
              {task.visibility
                .filter((v) => v !== 'me')
                .map((v) => VISIBILITY_LABELS[v])
                .join(', ')}
            </p>
          ) : null}
          {task.status === 'changes_requested' && task.rejection_reason ? (
            <p className="mt-1 rounded-token-sm bg-danger-soft/15 px-2 py-1 text-[11px] text-danger-soft-fg">
              Reason given: {task.rejection_reason}
            </p>
          ) : null}
          {task.submission_document_s3_key ? (
            <a
              href={`/api/documents/${task.submission_document_s3_key}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-medium text-accent hover:underline"
            >
              View submission
            </a>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          {task.status === 'awaiting_review' ? (
            <>
              <Button variant="primary" size="sm" onClick={approve} loading={busy}>
                Approve
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={onReject}
                disabled={busy}
              >
                Reject
              </Button>
            </>
          ) : null}
          <div className="mt-1 flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              disabled={busy}
              aria-label="Edit task"
              title="Edit"
              className="rounded p-1 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              aria-label="Remove task"
              title="Remove"
              className="rounded p-1 text-fg-muted transition-colors hover:bg-danger-soft/15 hover:text-danger disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}
